import React from "react";
import cn from "./helpers/cn";
import { TabsContext, } from "./Tabs";

export interface TabProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  value?: unknown;
  disabled?: boolean;
  wrapped?: boolean;
  selected?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  component?: React.ElementType;
  /** @internal — injetado por Tabs para suportar value por índice. */
  "data-tab-index"?: number;
}

const Tab = React.forwardRef<HTMLButtonElement, TabProps>((props, ref) => {
  const {
    className,
    component,
    label,
    icon,
    value: valueProp,
    disabled = false,
    wrapped = false,
    selected: selectedProp,
    // Aceitos por compatibilidade com a API do MUI v4; sem ripple para desligar.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disableFocusRipple,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    disableRipple,
    onClick,
    "data-tab-index": tabIndex,
    ...other
  } = props;

  const ctx = React.useContext(TabsContext);

  // Mesma lógica do MUI v4: se Tab tem `value`, usa-o; caso contrário, usa o índice.
  const tabValue = valueProp !== undefined ? valueProp : tabIndex;
  const selected = selectedProp !== undefined
    ? selectedProp
    : ctx
      ? ctx.value === tabValue
      : false;

  const Component = component ?? "button";
  const isButton = Component === "button";

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && ctx && !selected) {
      ctx.onChange(event, tabValue);
    }
    onClick?.(event);
  };

  const textColorClass = (() => {
    if (!ctx) return selected ? "text-primary" : "text-slate-700";

    if (ctx.textColor === "primary") return selected ? "text-primary" : "text-slate-700";
    if (ctx.textColor === "secondary") return selected ? "text-secondary" : "text-slate-700";

    return selected ? "text-current opacity-100" : "text-current opacity-70";
  })();

  const isVertical = ctx?.orientation === "vertical";
  const isFullWidth = ctx?.variant === "fullWidth";

  return (
    <Component
      ref={ref}
      role="tab"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-tab-selected={selected ? "true" : "false"}
      tabIndex={selected ? 0 : -1}
      {...(isButton ? { type: "button", disabled } : {})}
      onClick={handleClick}
      className={cn(
        // Base do MUI v4 Tab
        "relative inline-flex flex-col items-center justify-center shrink-0",
        "min-h-[48px] px-4 py-3 text-sm font-medium uppercase tracking-wide",
        "transition-colors duration-200 outline-none focus-visible:bg-black/[0.04]",
        "max-w-[264px] min-w-[72px]",
        isVertical && "min-w-[72px] w-full items-start",
        isFullWidth && !isVertical && "flex-1 max-w-none",
        wrapped && "text-xs leading-tight",
        textColorClass,
        disabled
          ? "opacity-50 cursor-not-allowed pointer-events-none"
          : "cursor-pointer hover:bg-black/[0.04]",
        className
      )}
      {...other}
    >
      {icon && (
        <span className={cn("flex items-center", label && "mb-1")} aria-hidden="true">
          {icon}
        </span>
      )}

      {label !== undefined && (
        <span className={cn(wrapped ? "whitespace-normal break-words" : "whitespace-nowrap")}>
          {label}
        </span>
      )}
    </Component>
  );
});

Tab.displayName = "Tab";

export default Tab;